import {
	fetchGroups,
	fetchItems,
	fetchList,
	fetchLists,
	GroupResponse,
	GroupsResponse,
	ItemResponse,
	ItemsResponse,
	ListResponse,
	ListsResponse,
	SortEntry,
} from "./api.ts";
import { TodoItem, TodoList, TodoListGroup } from "./types";
import { addToQueue, processQueue } from "./queue.ts";
import { getStore, setStore } from "./storage.ts";
import { v4 as uuid } from "uuid";
import { groupsState, itemsState, listsState, queueState, updateTaskCounts } from "@/states.ts";

export const updateStaleWhileRevalidate = async (
	fetchData: () => Promise<ListsResponse | ItemsResponse | GroupsResponse>,
	onData:
		| ((data: TodoItem[]) => void)
		| ((data: TodoList[]) => void)
		| ((data: TodoListGroup[]) => void),
) => {
	if (queueState.length > 0) {
		await processQueue();
	}
	// Don't overwrite optimistic state while mutations are still queued.
	if (navigator.onLine && queueState.length === 0) {
		// TODO handle error?
		fetchData()
			.then((r: any) => r.items || r.lists || r.groups)
			.then(onData);
	}
};
export const getLists = async () => {
	updateStaleWhileRevalidate(fetchLists, (lists: TodoList[]) => {
		listsState.lists = lists as TodoList[];
	});
	listsState.lists = getCachedLists();
};

export const getGroups = async () => {
	updateStaleWhileRevalidate(fetchGroups, (groups: TodoListGroup[]) => {
		groupsState.groups = groups as TodoListGroup[];
	});
	groupsState.groups = getCachedGroups();
};

export const getList = async (listId: string) => {
	fetchList(listId).then((r) => {
		if (r.ok) {
			const index = listsState.lists.findIndex((l) => l.id === listId);
			if (index === -1) {
				listsState.lists.push(r.list);
			} else {
				listsState.lists[index] = r.list;
			}
		}
	});
};

export const getItems = async (listId: string) => {
	updateStaleWhileRevalidate(
		() => fetchItems(listId),
		(items: TodoItem[]) => {
			itemsState.items[listId] = items as TodoItem[];
			updateTaskCounts(listId);
		},
	);
	itemsState.items[listId] = getCachedListItems(listId);
};

export const cacheLists = (lists: TodoList[]) => {
	setStore("lists", lists);
};

export const getCachedLists = (): TodoList[] => {
	return getStore("lists", []);
};

export const cacheGroups = (groups: TodoListGroup[]) => {
	setStore("groups", groups);
};

export const getCachedGroups = (): TodoListGroup[] => {
	return getStore("groups", []);
};

export const cacheListItems = (listId: string, items: TodoItem[]) => {
	setStore(`items-${listId}`, items);
};

export const getCachedListItems = (listId: string): TodoItem[] => {
	return getStore(`items-${listId}`, []);
};

export const createList = async (title: string, color: string): Promise<ListResponse> => {
	const newList = {
		id: uuid(),
		title,
		color,
		created_at: new Date().toISOString(),
		order: 0,
	};

	addToQueue({ value: newList, type: "list", action: "create" });

	return { ok: true, list: newList };
};

export const createGroup = async (title: string): Promise<GroupResponse> => {
	const newGroup = {
		id: uuid(),
		title,
		created_at: new Date().toISOString(),
	};

	addToQueue({ value: newGroup, type: "group", action: "create" });

	return { ok: true, group: newGroup };
};

export const updateGroup = async (group: TodoListGroup): Promise<GroupResponse> => {
	addToQueue({ value: group, type: "group", action: "update" });

	return { ok: true, group };
};

export const deleteGroup = async (group: TodoListGroup): Promise<{ ok: boolean }> => {
	addToQueue({ value: group, type: "group", action: "delete" });

	return { ok: true };
};

export const updateList = async (list: TodoList): Promise<ListResponse> => {
	addToQueue({ value: list, type: "list", action: "update" });

	return { ok: true, list };
};

export const deleteList = async (list: TodoList): Promise<{ ok: boolean }> => {
	addToQueue({ value: list, type: "list", action: "delete" });

	return { ok: true };
};

export const createItem = async (
	listId: string,
	text: string,
	order: number,
): Promise<ItemResponse> => {
	const newItem = { id: uuid(), text, order, list_id: listId };
	addToQueue({ value: newItem, listId, type: "item", action: "create" });

	return { ok: true, item: newItem };
};

export const updateItem = async (listId: string, value: TodoItem): Promise<ItemResponse> => {
	addToQueue({ value, listId, type: "item", action: "update" });

	return { ok: true, item: value };
};

export const deleteItem = async (listId: string, value: TodoItem): Promise<{ ok: boolean }> => {
	return deleteItems(listId, [value]);
};

export const deleteItems = async (listId: string, value: TodoItem[]): Promise<{ ok: boolean }> => {
	addToQueue({ value, listId, type: "item", action: "delete" });

	return { ok: true };
};

export const updateListsSort = async (entries: SortEntry[]) => {
	addToQueue({ value: entries, type: "list", action: "sort" });

	return { ok: true };
};

export const updateListItemsSort = async (listId: string, entries: SortEntry[]) => {
	addToQueue({ value: entries, listId, type: "item", action: "sort" });

	return { ok: true };
};
